import { FieldModel } from '@cibo/core'
import { AuthUserPermission, Can } from '@cibo/profile'
import { LandProfileDisplay } from '@cibo/ui'
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLandManagement } from '../../../'
import { FieldLabel } from '../../FieldLabel'
import { Farm } from './Farm'

interface MetadataProps {
  field: FieldModel
  onClickLabel: (label: string) => void
  onClickSupplyShed: (supplyShedId: string) => void
  onClickGrower: (growerContact: string) => void
  onClickSharedProfile: () => void
}

export const Metadata = ({
  field,
  onClickLabel,
  onClickSupplyShed,
  onClickGrower,
  onClickSharedProfile,
}: MetadataProps) => {
  const { t } = useTranslation('@cibo/landmanager/LandReport/Metadata')
  const { openAssignFarmDialog } = useLandManagement()

  return (
    <Grid spacing={2} container>
      <Grid item xs={12} md={8}>
        <Typography variant="h5" data-testid="land_report_field_name">
          {t('fieldPath', { state: field.state, county: field.county, name: field.name })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {LandProfileDisplay.locationDMS([field.longitude, field.latitude])}
        </Typography>

        <Can useAny={[AuthUserPermission.FARMS_VIEW]}>
          <Stack direction="row" spacing={1}>
            {!!field.farm && <Farm farmId={field.farm?.id} />}
            <Can useAny={[AuthUserPermission.FARMS_MANAGE]}>
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!field.can(['edit_meta'])}
                  onClick={() =>
                    openAssignFarmDialog({
                      resourceIds: [field.id],
                    })
                  }
                >
                  {t(!!field.farm ? 'edit' : 'assignFarm')}
                </Button>
              </Box>
            </Can>
          </Stack>
        </Can>

        <Stack spacing={1}>
          <Stack direction="row" spacing={0.5} sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
            {field.labels.length === 0 ? (
              <Typography variant="caption">{t('noLabels')}</Typography>
            ) : (
              field.labels.map((label, index) => (
                <FieldLabel
                  key={`${label}${index}`}
                  label={label}
                  onClick={() => onClickLabel(label)}
                  color="secondary"
                  size="small"
                />
              ))
            )}
          </Stack>
          {!!field.supplyShed && (
            <Stack direction="row" spacing={0.5} sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
              {field.supplyShed.map(supplyShed => (
                <Chip
                  key={supplyShed.resourceId}
                  color="info"
                  label={supplyShed.name}
                  onClick={() => onClickSupplyShed(supplyShed.resourceId)}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12} md={4}>
        <Stack alignItems={{ md: 'flex-end' }}>
          {!!field.ownedBy && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
              <Stack textAlign="right">
                <Typography>
                  {!!field.ownedBy.givenName || !!field.ownedBy.familyName
                    ? `${field.ownedBy.givenName} ${field.ownedBy.familyName}`
                    : field.ownedBy.userPrimaryEmail}
                </Typography>
                {(!!field.ownedBy.givenName || !!field.ownedBy.familyName) && (
                  <Typography color="text.secondary" variant="caption">
                    {field.ownedBy.userPrimaryEmail}
                  </Typography>
                )}
              </Stack>
              <Avatar alt={`${field.ownedBy.givenName} ${field.ownedBy.familyName}`} />
            </Stack>
          )}
          {!!field.sharedWith && (
            <AvatarGroup max={4} sx={{ justifyContent: 'flex-end' }}>
              {field.sharedWith.map(share => (
                <Avatar key={share.email} alt={`${share.givenName} ${share.familyName}`} />
              ))}
            </AvatarGroup>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('createdAtDate', { date: new Date(field.createdAt || '') })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('updatedAtDate', { date: new Date(field.updatedAt || '') })}
          </Typography>
          {field.can([AuthUserPermission.GROWER_CONTACT_UPDATE]) && field.grower && (
            <Link href={`mailto:${field.grower}`} variant="body2" color="secondary">
              {field.grower}
            </Link>
          )}
        </Stack>
      </Grid>
    </Grid>
  )
}
